<template>
    <font-awesome-icon v-if="isLoading" icon="spinner" spin/>
    <div v-else>
        <b-row v-if="hasAnyPermission(['goodsreceipt.create'])">
            <b-col class="mb-4">
                <OmniCard>
                    <template #header>
                        <OmniCardTitle class="mb-0">
                            Acties
                        </OmniCardTitle>
                    </template>

                    <OmniButtonCollection>
                        <OmniButton
                            v-if="hasPermission('goodsreceipt.create')"
                            :to="{ name: 'GoodsReceiptCreate' }"
                        >
                            Goederenontvangst aanmaken
                        </OmniButton>
                    </OmniButtonCollection>
                </OmniCard>
            </b-col>
        </b-row>

        <b-row>
            <b-col class="mb-4">
                <OmniCard>
                    <template #header>
                        <OmniCardTitle class="mb-0">
                            FILTERS
                        </OmniCardTitle>
                    </template>

                    <b-row>
                        <b-col cols="2">
                            <label>Afzender / leverancier</label><br/>
                            <b-select v-model="filterForm.supplier_location_id" :options="vendors" text-field="display_name" value-field="id" autocomplete="off"/>
                        </b-col>
                        <b-col cols="2">
                            <label>Is voltooid</label><br/>
                            <b-select v-model="filterForm.is_completed" :options="isCompletedOptions" autocomplete="off"/>
                        </b-col>
                    </b-row>
                </OmniCard>
            </b-col>
        </b-row>

        <b-row>
            <b-col>
                <b-card>
                    <b-card-title class="text-uppercase">{{ $t('omt.gui.general.label.results') }}</b-card-title>
                    <font-awesome-icon v-if="isFetchingGoodsReceipts" icon="spinner" spin/>
                    <b-alert v-else-if="!goodsReceipts.length" show variant="light">
                        Geen goederenontvangsten gevonden.
                    </b-alert>
                    <b-table-simple v-else>
                        <colgroup>
                            <col style="width:200px;"/>
                            <col style="width:200px;"/>
                            <col style="width:200px;"/>
                            <col/>
                            <col v-for="() in goodsReceipts[0].total_lines_attribute_count"/>
                        </colgroup>
                        <b-thead>
                            <b-tr>
                                <b-th>Aangemaakt op</b-th>
                                <b-th>Afzender</b-th>
                                <b-th>Identifier</b-th>
                                <b-th>Voltooid</b-th>
                                <b-th class="text-nowrap">Aantal crossdock-artikelen</b-th>
                                <b-th v-for="(count, attribute) in goodsReceipts[0].total_lines_attribute_count" class="text-nowrap">
                                    Aantal {{ attribute | toText }}
                                    <font-awesome-icon icon="circle-info" class="text-info ml-2"
                                                        v-b-popover.hover.top="'Wordt gevuld bij het aanmaken van de goederenontvangst en daarna niet meer geüpdatet.'"
                                    />
                                </b-th>
                            </b-tr>
                        </b-thead>
                        <b-tbody>
                            <b-tr v-for="goodsReceipt in goodsReceipts" :key="goodsReceipt.id">
                                <b-td>{{ goodsReceipt.created_at | datetimeshort }}</b-td>
                                <b-td>
                                    <location-link :location="goodsReceipt.supplier_location"/>
                                </b-td>
                                <b-td>
                                    <b-link :to="getGoodsReceiptRoute(goodsReceipt)">{{ goodsReceipt.identifier }}</b-link>
                                </b-td>
                                <b-td>
                                    {{ goodsReceipt.is_completed ? 'Ja' : 'Nee' }}
                                </b-td>
                                <b-td>
                                    {{ goodsReceipt.total_lines_crossdock_count }}
                                </b-td>
                                <b-td v-for="(count, attribute) in goodsReceipt.total_lines_attribute_count">
                                    {{ count }}
                                </b-td>
                            </b-tr>
                        </b-tbody>
                    </b-table-simple>
                </b-card>
            </b-col>
        </b-row>
    </div>
</template>

<script>
import PageTitle from "@/components/PageTitle.vue";
import LocationLink from "@/components/LocationLink.vue";
import JsConvert from "js-convert-case"

export default {
    name: "goodsReceiptIndex",

    components: {
        LocationLink,
        PageTitle
    },

    props: {
        locationTypeIdentifier: {
            type: String,
            required: true,
            default: ''
        },
        locationIdentifier: {
            type: String,
            required: true,
            default: ''
        },
    },

    data() {
        return {
            isLoading: true,
            isFetchingGoodsReceipts: false,
            filterForm: {
                supplier_location_id: '',
                is_completed: null,
            },
            goodsReceipts: [],
        }
    },

    mounted() {
        let promises = [
            this.$store.getters.getAllLocations,
            this.fetchGoodsReceipts({
                dispatcherType: this.locationTypeIdentifier,
                dispatcherIdentifier: this.locationIdentifier
            }),
        ]

        Promise.all(promises)
            .then(() => {
                this.isLoading = false
            })
    },

    computed: {
        vendors() {
            return [
                {
                    display_name: ' ',
                    id: '',
                },
                ..._.sortBy(this.$store.state.allLocations.filter(l => l.location_type.identifier === 'vendor' && l.active.toString() === '1'), 'display_name')
            ]
        },
        isCompletedOptions() {
            return {
                null: ' ',
                true: 'Ja',
                false: 'Nee',
            }
        },
    },

    methods: {
        async fetchGoodsReceipts({ dispatcherType, dispatcherIdentifier }) {
            if (!dispatcherType || !dispatcherIdentifier) {
                return Promise.resolve()
            }

            this.isFetchingGoodsReceipts = true
            this.goodsReceipts = [];

            const endpoint = `/api/rest/locations/${dispatcherType}/${dispatcherIdentifier}/goods-receipts`;
            return this.$http.get(endpoint, {
                    params: {
                        sort_by: 'created_at',
                        sort_dir: 'desc',
                        is_completed: this.filterForm.is_completed,
                        supplier_location_id: this.filterForm.supplier_location_id,
                        include:[
                            'total_lines_attribute_count',
                            'total_lines_crossdock_count',
                            'supplier_location.location_type',
                            'recipient_location.location_type',
                        ].join(',')
                    }
                })
                .then(response => {
                    const { data } = response;
                    this.goodsReceipts = [...data];
                })
                .catch(error => {
                    this.$alert(`Fout bij het ophalen van goederenontvangsten.`, 'danger');
                    console.error(`GoodsReceiptsIndex.vue: fetchGoodsReceipts(...params): ${error}`);
                })
                .finally(() => this.isFetchingGoodsReceipts = false)
        },
        getGoodsReceiptRoute(goodsReceipt) {
            return {
                name: 'GoodsReceiptView',
                params: {
                    locationTypeIdentifier: goodsReceipt.recipient_location.location_type.identifier,
                    locationIdentifier: goodsReceipt.recipient_location.identifier,
                    goodsReceiptIdentifier: goodsReceipt.identifier,
                },
            }
        },
    },

    filters: {
        toText(val) {
            return JsConvert.toTextCase(val)
        }
    },

    watch: {
        filterForm: {
            deep: true,
            handler() {
                this.fetchGoodsReceipts({
                    dispatcherType: this.locationTypeIdentifier,
                    dispatcherIdentifier: this.locationIdentifier
                })
            },
        }
    }
}
</script>

<style scoped lang="scss">
input[disabled],
select[disabled] {
    background: transparent;
    border-style: none;

    -moz-appearance: none;
    -webkit-appearance: none;

    &::-ms-expand {
        display: none;
    }
}
</style>
